import "./index.scss";
import profileImg from "../../images/defaultPic.png";
import axios from "axios";
import { selectAccessToken, selectRefreshToken } from "../../store/Auth";
import { useSelector, useDispatch } from "react-redux";
import { LoadingOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { login } from "../../store/Auth";

interface QuestionCardProps {
  question: string;
  author: {
    first_name: string;
    last_name: string;
  };
  time: string;
  isFacilitator: boolean;
  voteStatus: boolean;
  isAnswered?: boolean;
  id?: number;
  numberOfVotes?: number;
  questions?: any;
  setQuestions: React.Dispatch<React.SetStateAction<Question[]>>;
  activeID?: number;
  setActiveID: React.Dispatch<React.SetStateAction<number>>;
  picture: string;
}

type Question = {
  has_user_voted: boolean;
  id: number;
  is_anonymous: boolean;
  is_answered: boolean;
  lecture: number;
  number_of_votes: number;
  question_by: {} | null;
  question_ranking: number;
  question_text: string;
};

const QuestionCard: React.FunctionComponent<QuestionCardProps> = ({
  question,
  author,
  time,
  isFacilitator,
  voteStatus,
  isAnswered,
  id,
  numberOfVotes,
  questions,
  setQuestions,
  activeID,
  setActiveID,
  picture,
}) => {
  const token = useSelector(selectAccessToken);

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const refreshToken = useSelector(selectRefreshToken);

  const refreshAccessToken = () => {
    axios({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}/users/refresh-token`,
      data: {
        refresh: refreshToken,
      },
    })
      .then((res) => {
        dispatch(login(res.data));
      })
      .catch((err) => {
        navigate("/attendee/signin");
      });
  };

  const reloadQuestions = () => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/lectures/${params.id}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res: any) => {
        setQuestions(res.data.data.questions);
      })
      .catch((err) => {
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
      });
  };

  const markAsAnswered = () => {
    if (id) {
      setActiveID(id);
    }
    axios({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}/questions/mark-questions-answered/${id}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        const updatedQuestions = questions.filter(
          (question: any) => question.id !== id
        );
        setQuestions(updatedQuestions);
        notification.success({
          message: "Question marked as answered",
          description: "Question has been successfully marked as answered",
        });
      })
      .catch((err) => {
        setActiveID(-1);
        if (err.response.status === 401) {
          refreshAccessToken();
          return;
        }
        notification.error({
          message: "Error marking as answered",
          description:
            "Oops! there was an error marking the question as answered",
        });
      });
  };

  const upvote = () => {
    if (!isFacilitator && id && !voteStatus) {
      setActiveID(id);

      axios({
        method: "post",
        url: `${process.env.REACT_APP_BASE_URL}/questions/upvote-question/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          reloadQuestions();
          setActiveID(-1);
          notification.success({
            message: "Question upvoted",
            description: "Question has been successfully upvoted",
          });
        })
        .catch((err) => {
          setActiveID(-1);
          if (err.response.status === 401) {
            refreshAccessToken();
            return;
          }
          notification.error({
            message: "Error upvoting question",
            description: "Oops! there was an error upvoting the question",
          });
        });
    }
  };

  return (
    <div className="fullCard">
      <div className="innerCard">
        <div className="flxTp">
          <img src={author ? picture : profileImg} alt="" />
          <div className="rghtSd">
            <p>{question}</p>
            <div className="flxTimeSt">
              <div className="post">
                <span className="author">
                  Posted by:{" "}
                  <span>
                    {author
                      ? `${author.first_name} ${author.last_name}`
                      : "Anonymous"}
                  </span>
                </span>
              </div>
              <div className="time">
                <span className="tme">
                  Time stamp:{" "}
                  <span className="ct">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                        stroke="#2563EB"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 4.6665V7.99984H11.3333"
                        stroke="#2563EB"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{time}</span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flexWActs">
        <button className="upvote" onClick={upvote}>
          {isFacilitator ? (
            <div className="facUpvts">
              <svg
                width="16"
                height="18"
                viewBox="0 0 16 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_962_8867)">
                  <path
                    d="M2.66667 14C2.66667 13.8194 2.60069 13.6632 2.46875 13.5312C2.33681 13.3993 2.18056 13.3333 2 13.3333C1.81944 13.3333 1.66319 13.3993 1.53125 13.5312C1.39931 13.6632 1.33333 13.8194 1.33333 14C1.33333 14.1806 1.39931 14.3368 1.53125 14.4688C1.66319 14.6007 1.81944 14.6667 2 14.6667C2.18056 14.6667 2.33681 14.6007 2.46875 14.4688C2.60069 14.3368 2.66667 14.1806 2.66667 14ZM14.6667 8C14.6667 7.64583 14.5312 7.33507 14.2604 7.06771C13.9896 6.80035 13.6806 6.66667 13.3333 6.66667H9.66667C9.66667 6.26389 9.83333 5.71007 10.1667 5.00521C10.5 4.30035 10.6667 3.74306 10.6667 3.33333C10.6667 2.65278 10.5556 2.14931 10.3333 1.82292C10.1111 1.49653 9.66667 1.33333 9 1.33333C8.81944 1.51389 8.6875 1.80903 8.60417 2.21875C8.52083 2.62847 8.41493 3.06424 8.28646 3.52604C8.15799 3.98785 7.95139 4.36806 7.66667 4.66667C7.51389 4.82639 7.24653 5.14236 6.86458 5.61458C6.83681 5.64931 6.75694 5.75347 6.625 5.92708C6.49306 6.10069 6.38368 6.24306 6.29688 6.35417C6.21007 6.46528 6.09028 6.61285 5.9375 6.79688C5.78472 6.9809 5.64583 7.13368 5.52083 7.25521C5.39583 7.37674 5.26215 7.5 5.11979 7.625C4.97743 7.75 4.83854 7.84375 4.70312 7.90625C4.56771 7.96875 4.44444 8 4.33333 8H4V14.6667H4.33333C4.42361 14.6667 4.53299 14.6771 4.66146 14.6979C4.78993 14.7188 4.90451 14.7413 5.00521 14.7656C5.1059 14.7899 5.23785 14.8281 5.40104 14.8802C5.56424 14.9323 5.68576 14.9722 5.76562 15C5.84549 15.0278 5.96875 15.0712 6.13542 15.1302C6.30208 15.1892 6.40278 15.2257 6.4375 15.2396C7.90278 15.7465 9.09028 16 10 16H11.2604C12.5938 16 13.2604 15.4201 13.2604 14.2604C13.2604 14.0799 13.2431 13.8854 13.2083 13.6771C13.4167 13.566 13.5816 13.3837 13.7031 13.1302C13.8247 12.8767 13.8854 12.6215 13.8854 12.3646C13.8854 12.1076 13.8229 11.8681 13.6979 11.6458C14.066 11.2986 14.25 10.8854 14.25 10.4062C14.25 10.2326 14.2153 10.0399 14.1458 9.82812C14.0764 9.61632 13.9896 9.45139 13.8854 9.33333C14.1076 9.32639 14.2934 9.16319 14.4427 8.84375C14.592 8.52431 14.6667 8.24306 14.6667 8ZM16 7.98958C16 8.60764 15.8299 9.17361 15.4896 9.6875C15.5521 9.91667 15.5833 10.1562 15.5833 10.4062C15.5833 10.941 15.4514 11.441 15.1875 11.9062C15.2083 12.0521 15.2188 12.2014 15.2188 12.3542C15.2188 13.0556 15.0104 13.6736 14.5938 14.2083C14.6007 15.1736 14.3056 15.9358 13.7083 16.4948C13.1111 17.0538 12.3229 17.3333 11.3438 17.3333H10C9.33333 17.3333 8.67535 17.2552 8.02604 17.099C7.37674 16.9427 6.625 16.7153 5.77083 16.4167C4.96528 16.1389 4.48611 16 4.33333 16H1.33333C0.965278 16 0.651042 15.8698 0.390625 15.6094C0.130208 15.349 0 15.0347 0 14.6667V8C0 7.63194 0.130208 7.31771 0.390625 7.05729C0.651042 6.79688 0.965278 6.66667 1.33333 6.66667H4.1875C4.4375 6.5 4.91319 5.96181 5.61458 5.05208C6.01736 4.53125 6.38889 4.08681 6.72917 3.71875C6.89583 3.54514 7.0191 3.24826 7.09896 2.82812C7.17882 2.40799 7.28472 1.96875 7.41667 1.51042C7.54861 1.05208 7.76389 0.677083 8.0625 0.385417C8.33333 0.128472 8.64583 0 9 0C9.58333 0 10.1076 0.112847 10.5729 0.338542C11.0382 0.564236 11.3924 0.916667 11.6354 1.39583C11.8785 1.875 12 2.52083 12 3.33333C12 3.97917 11.8333 4.64583 11.5 5.33333H13.3333C14.0556 5.33333 14.6806 5.59722 15.2083 6.125C15.7361 6.65278 16 7.27431 16 7.98958Z"
                    fill="#14B8A6"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_962_8867">
                    <rect width="16" height="17.3333" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <p className="numVots">{numberOfVotes}</p>
            </div>
          ) : (
            <>
              {voteStatus ? (
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="20" cy="20" r="20" fill="#14B8A6" />
                  <g clipPath="url(#clip0_958_8451)">
                    <path
                      d="M13.3333 26.5C13.3333 26.2743 13.2509 26.079 13.0859 25.9141C12.921 25.7491 12.7257 25.6667 12.5 25.6667C12.2743 25.6667 12.079 25.7491 11.9141 25.9141C11.7491 26.079 11.6667 26.2743 11.6667 26.5C11.6667 26.7257 11.7491 26.921 11.9141 27.0859C12.079 27.2509 12.2743 27.3333 12.5 27.3333C12.7257 27.3333 12.921 27.2509 13.0859 27.0859C13.2509 26.921 13.3333 26.7257 13.3333 26.5ZM28.3333 19C28.3333 18.5573 28.1641 18.1688 27.8255 17.8346C27.487 17.5004 27.1007 17.3333 26.6667 17.3333H22.0833C22.0833 16.8299 22.2917 16.1376 22.7083 15.2565C23.125 14.3754 23.3333 13.6788 23.3333 13.1667C23.3333 12.316 23.1944 11.6866 22.9167 11.2786C22.6389 10.8707 22.0833 10.6667 21.25 10.6667C21.0243 10.8924 20.8594 11.2613 20.7552 11.7734C20.651 12.2856 20.5187 12.8303 20.3581 13.4076C20.1975 13.9848 19.9392 14.4601 19.5833 14.8333C19.3924 15.033 19.0582 15.428 18.5807 16.0182C18.546 16.0616 18.4462 16.1918 18.2812 16.4089C18.1163 16.6259 17.9796 16.8038 17.8711 16.9427C17.7626 17.0816 17.6128 17.2661 17.4219 17.4961C17.2309 17.7261 17.0573 17.9171 16.901 18.069C16.7448 18.2209 16.5777 18.375 16.3997 18.5312C16.2218 18.6875 16.0482 18.8047 15.8789 18.8828C15.7096 18.9609 15.5556 19 15.4167 19H15V27.3333H15.4167C15.5295 27.3333 15.6662 27.3464 15.8268 27.3724C15.9874 27.3984 16.1306 27.4266 16.2565 27.457C16.3824 27.4874 16.5473 27.5352 16.7513 27.6003C16.9553 27.6654 17.1072 27.7153 17.207 27.75C17.3069 27.7847 17.4609 27.839 17.6693 27.9128C17.8776 27.9865 18.0035 28.0321 18.0469 28.0495C19.8785 28.6832 21.3628 29 22.5 29H24.0755C25.7422 29 26.5755 28.2752 26.5755 26.8255C26.5755 26.5998 26.5538 26.3568 26.5104 26.0964C26.7708 25.9575 26.977 25.7296 27.1289 25.4128C27.2808 25.0959 27.3568 24.7769 27.3568 24.4557C27.3568 24.1345 27.2786 23.8351 27.1224 23.5573C27.5825 23.1233 27.8125 22.6068 27.8125 22.0078C27.8125 21.7908 27.7691 21.5499 27.6823 21.2852C27.5955 21.0204 27.487 20.8142 27.3568 20.6667C27.6345 20.658 27.8668 20.454 28.0534 20.0547C28.24 19.6554 28.3333 19.3038 28.3333 19ZM30 18.987C30 19.7595 29.7873 20.467 29.362 21.1094C29.4401 21.3958 29.4792 21.6953 29.4792 22.0078C29.4792 22.6762 29.3142 23.3012 28.9844 23.8828C29.0104 24.0651 29.0234 24.2517 29.0234 24.4427C29.0234 25.3194 28.763 26.092 28.2422 26.7604C28.2509 27.967 27.8819 28.9197 27.1354 29.6185C26.3889 30.3173 25.4036 30.6667 24.1797 30.6667H22.5C21.6667 30.6667 20.8442 30.569 20.0326 30.3737C19.2209 30.1784 18.2812 29.8941 17.2135 29.5208C16.2066 29.1736 15.6076 29 15.4167 29H11.6667C11.2066 29 10.8138 28.8372 10.4883 28.5117C10.1628 28.1862 10 27.7934 10 27.3333V19C10 18.5399 10.1628 18.1471 10.4883 17.8216C10.8138 17.4961 11.2066 17.3333 11.6667 17.3333H15.2344C15.5469 17.125 16.1415 16.4523 17.0182 15.3151C17.5217 14.6641 17.9861 14.1085 18.4115 13.6484C18.6198 13.4314 18.7739 13.0603 18.8737 12.5352C18.9735 12.01 19.1059 11.4609 19.2708 10.888C19.4358 10.3151 19.7049 9.84635 20.0781 9.48177C20.4167 9.16059 20.8073 9 21.25 9C21.9792 9 22.6345 9.14106 23.2161 9.42318C23.7977 9.7053 24.2405 10.1458 24.5443 10.7448C24.8481 11.3438 25 12.151 25 13.1667C25 13.974 24.7917 14.8073 24.375 15.6667H26.6667C27.5694 15.6667 28.3507 15.9965 29.0104 16.6562C29.6701 17.316 30 18.0929 30 18.987Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_958_8451">
                      <rect
                        width="20"
                        height="21.6667"
                        fill="white"
                        transform="translate(10 9)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              ) : (
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="20"
                    cy="20"
                    r="19.5"
                    fill="white"
                    stroke="#9DA3AB"
                  />
                  <g clipPath="url(#clip0_958_8480)">
                    <path
                      d="M13.3333 26.5C13.3333 26.2743 13.2509 26.079 13.0859 25.9141C12.921 25.7491 12.7257 25.6667 12.5 25.6667C12.2743 25.6667 12.079 25.7491 11.9141 25.9141C11.7491 26.079 11.6667 26.2743 11.6667 26.5C11.6667 26.7257 11.7491 26.921 11.9141 27.0859C12.079 27.2509 12.2743 27.3333 12.5 27.3333C12.7257 27.3333 12.921 27.2509 13.0859 27.0859C13.2509 26.921 13.3333 26.7257 13.3333 26.5ZM28.3333 19C28.3333 18.5573 28.1641 18.1688 27.8255 17.8346C27.487 17.5004 27.1007 17.3333 26.6667 17.3333H22.0833C22.0833 16.8299 22.2917 16.1376 22.7083 15.2565C23.125 14.3754 23.3333 13.6788 23.3333 13.1667C23.3333 12.316 23.1944 11.6866 22.9167 11.2786C22.6389 10.8707 22.0833 10.6667 21.25 10.6667C21.0243 10.8924 20.8594 11.2613 20.7552 11.7734C20.651 12.2856 20.5187 12.8303 20.3581 13.4076C20.1975 13.9848 19.9392 14.4601 19.5833 14.8333C19.3924 15.033 19.0582 15.428 18.5807 16.0182C18.546 16.0616 18.4462 16.1918 18.2812 16.4089C18.1163 16.6259 17.9796 16.8038 17.8711 16.9427C17.7626 17.0816 17.6128 17.2661 17.4219 17.4961C17.2309 17.7261 17.0573 17.9171 16.901 18.069C16.7448 18.2209 16.5777 18.375 16.3997 18.5312C16.2218 18.6875 16.0482 18.8047 15.8789 18.8828C15.7096 18.9609 15.5556 19 15.4167 19H15V27.3333H15.4167C15.5295 27.3333 15.6662 27.3464 15.8268 27.3724C15.9874 27.3984 16.1306 27.4266 16.2565 27.457C16.3824 27.4874 16.5473 27.5352 16.7513 27.6003C16.9553 27.6654 17.1072 27.7153 17.207 27.75C17.3069 27.7847 17.4609 27.839 17.6693 27.9128C17.8776 27.9865 18.0035 28.0321 18.0469 28.0495C19.8785 28.6832 21.3628 29 22.5 29H24.0755C25.7422 29 26.5755 28.2752 26.5755 26.8255C26.5755 26.5998 26.5538 26.3568 26.5104 26.0964C26.7708 25.9575 26.977 25.7296 27.1289 25.4128C27.2808 25.0959 27.3568 24.7769 27.3568 24.4557C27.3568 24.1345 27.2786 23.8351 27.1224 23.5573C27.5825 23.1233 27.8125 22.6068 27.8125 22.0078C27.8125 21.7908 27.7691 21.5499 27.6823 21.2852C27.5955 21.0204 27.487 20.8142 27.3568 20.6667C27.6345 20.658 27.8668 20.454 28.0534 20.0547C28.24 19.6554 28.3333 19.3038 28.3333 19ZM30 18.987C30 19.7595 29.7873 20.467 29.362 21.1094C29.4401 21.3958 29.4792 21.6953 29.4792 22.0078C29.4792 22.6762 29.3142 23.3012 28.9844 23.8828C29.0104 24.0651 29.0234 24.2517 29.0234 24.4427C29.0234 25.3194 28.763 26.092 28.2422 26.7604C28.2509 27.967 27.8819 28.9197 27.1354 29.6185C26.3889 30.3173 25.4036 30.6667 24.1797 30.6667H22.5C21.6667 30.6667 20.8442 30.569 20.0326 30.3737C19.2209 30.1784 18.2812 29.8941 17.2135 29.5208C16.2066 29.1736 15.6076 29 15.4167 29H11.6667C11.2066 29 10.8138 28.8372 10.4883 28.5117C10.1628 28.1862 10 27.7934 10 27.3333V19C10 18.5399 10.1628 18.1471 10.4883 17.8216C10.8138 17.4961 11.2066 17.3333 11.6667 17.3333H15.2344C15.5469 17.125 16.1415 16.4523 17.0182 15.3151C17.5217 14.6641 17.9861 14.1085 18.4115 13.6484C18.6198 13.4314 18.7739 13.0603 18.8737 12.5352C18.9735 12.01 19.1059 11.4609 19.2708 10.888C19.4358 10.3151 19.7049 9.84635 20.0781 9.48177C20.4167 9.16059 20.8073 9 21.25 9C21.9792 9 22.6345 9.14106 23.2161 9.42318C23.7977 9.7053 24.2405 10.1458 24.5443 10.7448C24.8481 11.3438 25 12.151 25 13.1667C25 13.974 24.7917 14.8073 24.375 15.6667H26.6667C27.5694 15.6667 28.3507 15.9965 29.0104 16.6562C29.6701 17.316 30 18.0929 30 18.987Z"
                      fill="#9DA3AB"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_958_8480">
                      <rect
                        width="20"
                        height="21.6667"
                        fill="white"
                        transform="translate(10 9)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              )}
            </>
          )}
          {isFacilitator ? (
            <span className="btnName">
              <span className="upvoted">Upvotes</span>
            </span>
          ) : (
            <span className="btnName">
              {voteStatus ? (
                <span className="upvoted">Upvoted</span>
              ) : (
                <span className="upvote">
                  {activeID === id ? <LoadingOutlined /> : "Upvote"}
                </span>
              )}
            </span>
          )}
        </button>
        {isFacilitator && (
          <div className="facBtns">
            {!isAnswered ? (
              <button className="answ" onClick={markAsAnswered}>
                {activeID === id ? (
                  <LoadingOutlined />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 12.0001L8.94975 16.9499L19.5563 6.34326"
                      stroke="#2563EB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <span>Mark as Answered</span>
              </button>
            ) : (
              <button className="bluAns">Answered</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;
